import React, { useEffect, useState } from 'react';
import { Modal } from '../Modal/Modal.component';
import { Grid, TextField } from '@material-ui/core';
import { useLayoutContext } from '../../Context/Layout.context';
import { confirmUpdateUserPassword } from '../../services/users/users.service';
import { UpdateButton } from '../UserProfile/UpdateButton.component';
import { isPeselValid } from '../../utils/peselValidator';
import { useStyles } from '../UserProfile/EditUserPhone.styles';
import { AxiosError } from 'axios';
import { ErrorCode, BackendErrorCode, BackendErrorErrorMessage } from './AccountError.types';
import _ from 'lodash';
import { Paths } from '../../utils/routerUrls';

interface AuthUserPeselProps {
  open: boolean;
  password: string;
  token: string;
  email: string;
  onClose(): void;
  onConfirm(): void;
}

interface ResponseErrors {
  code: BackendErrorCode;
  description: string;
}

export const AuthUserPesel: React.FC<AuthUserPeselProps> = (props) => {
  const classes = useStyles();

  const [pesel, setPesel] = useState<string>('');
  const [validPeselFormat, setValidPeselFormat] = useState<boolean>(true);
  const [uniquePesel, setUniquePesel] = useState<boolean>(true);
  const [newInput, setIsNewInput] = useState<boolean>(false);
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(true);
  const [errorCodes, setErrorCodes] = useState<ErrorCode[]>([]);

  const { openAlert, showLoader } = useLayoutContext();

  const handleChangePesel = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPesel(event.target.value);
    setIsNewInput(true);
    setValidPeselFormat(isPeselValid(pesel));
  };

  const clearInput = () => {
    setPesel('');
    setErrorCodes([]);
    setIsError(false);
    setIsUpdateButtonDisabled(true);
  };

  const handleUpdateUserPassword = () => {
    setErrorCodes([]);
    showLoader(true, 'Weryfikuję dane');
    setUniquePesel(true);
    setIsNewInput(false);
    confirmUpdateUserPassword(props.token, props.password, props.email, pesel)
      .then(
        () => {
          openAlert(
            'success',
            <div style={{ display: 'inline' }}>
              Dane zostały pomyślnie zapisane. Za chwilę nastąpi automatyczne przekierowanie do ekranu logowania. Jeśli
              nie chcesz czekać, kliknij w <a href={Paths.logout}>link</a>
            </div>,
          );
          props.onConfirm();
          props.onClose();
        },
        (error: AxiosError) => {
          if (error.response?.data.errors) {
            setIsError(true);
            setIsUpdateButtonDisabled(true);
            openAlert('save_error');
            const validationErrorCodes = error.response?.data.errors.map((error: ResponseErrors) => error.code);
            setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, ...validationErrorCodes]));
          } else {
            props.onClose();
            openAlert('error');
          }
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  useEffect(() => {
    if (newInput && pesel !== '') {
      setIsError(false);
      setIsUpdateButtonDisabled(false);
      setErrorCodes([]);
    } else if (pesel === '' || !validPeselFormat || !uniquePesel || pesel.length <= 10) {
      setIsUpdateButtonDisabled(true);
    } else setIsUpdateButtonDisabled(false);
  }, [newInput, pesel, validPeselFormat, uniquePesel]);

  useEffect(() => {
    if ((!validPeselFormat || !uniquePesel) && pesel !== '') {
      setIsError(true);
    } else {
      setIsError(false);
      setErrorCodes([]);
    }
  }, [validPeselFormat || uniquePesel]);

  const getErrorMessage = () => {
    if (errorCodes.includes(BackendErrorCode.WrongPesel)) {
      return <div className={classes.errorMessage}> {BackendErrorErrorMessage.WrongPesel} </div>;
    } else if (errorCodes.includes(BackendErrorCode.ExpiredAccount)) {
      return <div className={classes.errorMessage}> {BackendErrorErrorMessage.ExpiredAccount} </div>;
    } else return <></>;
  };

  return (
    <>
      <Modal
        open={props.open}
        size="xs"
        xCloseButton={() => {
          clearInput();
          props.onClose();
        }}
        autoHeight={true}
        isHideBackdrop={true}
      >
        <Grid container className={classes.container} style={{ paddingBottom: '10px' }}>
          <Grid>
            <div className={classes.modalSubTitle}>W celu weryfikacji podaj numer PESEL</div>
          </Grid>
          <Grid item xs={12} className={classes.item} style={{ marginBottom: '20px' }}>
            <TextField
              error={isError}
              helperText={getErrorMessage()}
              value={pesel}
              onChange={handleChangePesel}
              id="PESEL"
              label="PESEL"
              placeholder="PESEL"
              variant="outlined"
              fullWidth={true}
              inputProps={{
                style: {
                  padding: '15.5px',
                  color: '#2C2D3E',
                  borderColor: isError ? '#BC0000' : '#2C2D3E',
                },
              }}
              FormHelperTextProps={{
                style: {
                  margin: '5px',
                  color: '#BC0000',
                  height: '30px',
                  position: 'absolute',
                  top: '48px',
                },
              }}
              InputLabelProps={{
                style: { color: isError ? '#BC0000' : '#2C2D3E' },
              }}
            />
          </Grid>
          <Grid className={classes.verifyButton} item xs={9}>
            <UpdateButton text="Zweryfikuj" isDisabled={isUpdateButtonDisabled} onClick={handleUpdateUserPassword} />
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};
